if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();

        if (this.hasAttribute('data-cca-mobile-side-rail')) {
          this.sideRailMql = window.matchMedia('(max-width: 719px)');
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend, scrollPage = true) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (!scrollPage || activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        const previousThumbnail = this.elements.thumbnails
          .querySelector('button[aria-current]')
          ?.closest('[data-target]');

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (this.hasAttribute('data-cca-mobile-side-rail') && this.sideRailMql.matches) {
          const rail = this.elements.thumbnails.slider;
          const thumbnails = Array.from(rail.querySelectorAll('[data-target]'));
          const thumbnailIndex = thumbnails.indexOf(thumbnail);
          const previousIndex = thumbnails.indexOf(previousThumbnail);
          const visibleCount = Math.max(
            1,
            Number.parseInt(getComputedStyle(this).getPropertyValue('--cca-visible-thumbnails'), 10) || 5
          );
          const itemStep = thumbnails.length > 1 ? thumbnails[1].offsetTop - thumbnails[0].offsetTop : 0;
          const firstVisibleIndex = itemStep > 0 ? Math.round(rail.scrollTop / itemStep) : 0;
          const lastVisibleIndex = Math.min(thumbnails.length - 1, firstVisibleIndex + visibleCount - 1);
          const isVisible = thumbnailIndex >= firstVisibleIndex && thumbnailIndex <= lastVisibleIndex;
          const movingForward = previousIndex >= 0 && thumbnailIndex > previousIndex;
          const movingBackward = previousIndex >= 0 && thumbnailIndex < previousIndex;
          const shouldAdvance =
            isVisible && movingForward && thumbnailIndex === lastVisibleIndex && thumbnailIndex < thumbnails.length - 1;
          const shouldReverse = isVisible && movingBackward && thumbnailIndex === firstVisibleIndex && thumbnailIndex > 0;
          let nextFirstIndex;

          if (shouldAdvance) {
            nextFirstIndex = thumbnailIndex;
          } else if (shouldReverse) {
            nextFirstIndex = Math.max(0, thumbnailIndex - visibleCount + 1);
          } else if (!isVisible) {
            nextFirstIndex =
              thumbnailIndex < firstVisibleIndex
                ? thumbnailIndex
                : Math.max(0, thumbnailIndex - visibleCount + 1);
          }

          if (nextFirstIndex !== undefined) {
            const target = thumbnails[nextFirstIndex];
            const scrollTop = target?.offsetTop ?? 0;
            rail.scrollTo({
              top: Math.min(Math.max(0, scrollTop), Math.max(0, rail.scrollHeight - rail.clientHeight)),
              behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
            });
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
